import { ComponentProps } from "react";

export type YesNoOptionBase = {
  value: string | number;
  label: string;
};

export type Props = Omit<ComponentProps<"div">, "onChange"> & {
  /** Field name for identification */
  name: string;
  /** Options (typically Yes/No but can be customized) */
  options: YesNoOptionBase[];
  /** Currently selected value (as array with single item for consistency) */
  value?: (string | number)[];
  /** Change handler - receives array with selected value */
  onChange?: (value: (string | number)[]) => void;
  /** Blur handler */
  onBlur?: () => void;
  /** Whether the field has an error (for styling and aria-invalid) */
  error?: boolean;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** ID of the element that describes this group (typically error message) */
  "aria-describedby"?: string;
  /** Render function for each button */
  renderButton?: (props: {
    option: YesNoOptionBase;
    isSelected: boolean;
    isDisabled: boolean;
    onClick: () => void;
  }) => React.ReactNode;
};

/**
 * Base yes/no buttons component with no styling.
 * Use this as the foundation for styled yes/no button components.
 *
 * This is essentially a single-select radio group displayed as buttons.
 */
export function YesNoButtonsBase({
  name,
  options,
  value = [],
  onChange,
  onBlur,
  error,
  disabled = false,
  renderButton,
  className,
  id,
  ...props
}: Props) {
  const selectedValue = value.length > 0 ? value[0] : undefined;

  const handleClick = (optionValue: string | number) => {
    onChange?.([optionValue]);
  };

  // Default render if no custom render function provided
  const defaultRenderButton = ({
    option,
    isSelected,
    isDisabled,
    onClick,
  }: {
    option: YesNoOptionBase;
    isSelected: boolean;
    isDisabled: boolean;
    onClick: () => void;
  }) => (
    <button
      key={option.value}
      type="button"
      onClick={onClick}
      disabled={isDisabled}
      aria-pressed={isSelected}
      data-testid={`yesno-${name}-${option.label.toLowerCase()}`}
    >
      {option.label}
    </button>
  );

  const render = renderButton ?? defaultRenderButton;

  return (
    <div
      role="radiogroup"
      aria-labelledby={id ? `${id}-label` : undefined}
      aria-invalid={error || undefined}
      className={className}
      onBlur={onBlur}
      data-testid={`yesno-${name}`}
      {...props}
    >
      {options.map((option) => {
        const isSelected = selectedValue === option.value;

        return render({
          option,
          isSelected,
          isDisabled: disabled,
          onClick: () => handleClick(option.value),
        });
      })}
    </div>
  );
}

YesNoButtonsBase.displayName = "YesNoButtonsBase";

